/**
 * Royalty Report Tab - Owner Only
 * 
 * Calculates and tracks 4% royalty payments from Bulaccino Café to Main Bulaccino store.
 * Based on gross sales (money actually collected) minus complimentary items and VAT.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DollarSign, Calendar, Download, Check, Clock, FileText, 
  RefreshCw, AlertCircle, ChevronDown, ChevronUp, Loader2,
  Building, Receipt, TrendingUp, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Helper to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-FJ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

// Helper to get month options (last 24 months)
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    months.push({ value, label });
  }
  return months;
};

// Current month as default (since cafe just opened, there won't be data for previous months)
const getDefaultMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export default function RoyaltyReportTab() {
  const [selectedMonth, setSelectedMonth] = useState(getDefaultMonth());
  const [calculation, setCalculation] = useState(null);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: '',
    payment_notes: ''
  });
  const [expandedYear, setExpandedYear] = useState(new Date().getFullYear().toString());

  // Fetch database diagnostic
  const fetchDiagnostic = useCallback(async () => {
    try {
      const res = await api.get('/admin/analytics/db-diagnostic');
      setDiagnostic(res.data);
    } catch (err) {
      console.error('Failed to fetch diagnostic:', err);
    }
  }, []);
  
  // Fetch calculation for selected month
  const fetchCalculation = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/royalty/calculate/${selectedMonth}`);
      setCalculation(res.data);
      
      // If calculation shows $0, auto-fetch diagnostic
      if (res.data.gross_sales_collected === 0) {
        fetchDiagnostic();
        setShowDiagnostic(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to calculate royalty');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, fetchDiagnostic]);
  
  // Fetch payment history
  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/admin/royalty/history');
      setHistory(res.data.records || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, []);
  
  useEffect(() => {
    fetchCalculation();
    fetchHistory();
  }, [fetchCalculation, fetchHistory]);
  
  // Save/create record for month
  const handleSaveRecord = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/royalty/record/${selectedMonth}`);
      toast.success('Royalty record saved');
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save record');
    } finally {
      setLoading(false);
    }
  };
  
  // Mark as paid
  const handleMarkAsPaid = async () => {
    setLoading(true);
    try {
      await api.patch(`/admin/royalty/mark-paid/${selectedMonth}`, paymentData);
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setLoading(false);
    }
  };
  
  // Download PDF report
  const handleDownloadPDF = async () => {
    try {
      const res = await api.get(`/admin/royalty/report/${selectedMonth}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `royalty-report-${selectedMonth}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Report downloaded - open in browser and print to PDF');
    } catch (err) {
      toast.error('Failed to generate report');
    }
  };
  
  // Get record for selected month from history
  const currentRecord = history.find(r => r.month === selectedMonth);
  const isPaid = currentRecord?.status === 'paid';
  
  // Group history by year for display
  const historyByYear = history.reduce((acc, record) => {
    const year = record.month?.substring(0, 4);
    if (!acc[year]) acc[year] = [];
    acc[year].push(record);
    return acc;
  }, {});
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">4% Royalty Report</h2>
          <p className="text-muted-foreground">
            License fee from Bulaccino Café (Namaka) to Bulaccino (Nadi Plaza)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchCalculation(); fetchHistory(); }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Paid</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_paid)} FJD</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Pending</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(summary.total_owed)} FJD</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <History className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Months Recorded</p>
                  <p className="text-2xl font-bold text-foreground">{summary.total_records}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Month Selection & Calculation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Monthly Royalty Calculation
          </CardTitle>
          <CardDescription>
            Select a month to calculate the 4% royalty based on money collected
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Month Selector */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label>Select Month</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={fetchCalculation} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Calculate'}
              </Button>
            </div>
          </div>
          
          {/* Calculation Results */}
          {calculation && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-muted/50 rounded-xl p-6 space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gross Sales Collected:</span>
                    <span className="font-medium">{formatCurrency(calculation.gross_sales_collected)} FJD</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Less: VAT (12.5%):</span>
                    <span className="text-red-500">-{formatCurrency(calculation.vat_amount)} FJD</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-muted-foreground">Net Sales (Royalty Base):</span>
                    <span className="font-medium">{formatCurrency(calculation.sales_excluding_vat)} FJD</span>
                  </div>
                </div>
                
                <div className="flex flex-col items-center justify-center bg-primary/10 rounded-xl p-4">
                  <p className="text-sm text-muted-foreground mb-1">4% Royalty Due</p>
                  <p className="text-3xl font-bold text-primary">{formatCurrency(calculation.royalty_amount)} FJD</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {calculation.total_transactions || calculation.order_count} transactions
                    {calculation.coffee_pass_count > 0 && (
                      <span className="ml-1">(incl. {calculation.coffee_pass_count} Coffee Pass)</span>
                    )}
                  </p>
                </div>
              </div>
              
              {/* Debug Info - Shows when $0 to help troubleshoot */}
              {calculation.gross_sales_collected === 0 && calculation.debug && (
                <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-300 mb-2">⚠️ No sales found for this month. Debug info:</p>
                  <ul className="text-amber-700 dark:text-amber-400 space-y-1 text-xs font-mono">
                    <li>Date range queried: {calculation.debug.date_range_queried}</li>
                    <li>Total orders in DB: {calculation.debug.total_orders_in_db}</li>
                    <li>Orders matching date range: {calculation.debug.orders_in_range}</li>
                    <li>Paid orders counted: {calculation.debug.paid_orders_counted}</li>
                    {calculation.debug.sample_order_dates?.length > 0 && (
                      <li>Sample order dates: {calculation.debug.sample_order_dates.join(', ')}</li>
                    )}
                  </ul>
                </div>
              )}
              
              {/* Status Badge */}
              {currentRecord && (
                <div className={`flex items-center gap-2 p-3 rounded-lg ${
                  isPaid ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
                }`}>
                  {isPaid ? (
                    <>
                      <Check className="w-5 h-5 text-green-600" />
                      <span className="text-green-700 dark:text-green-400 font-medium">
                        Paid on {currentRecord.payment_date?.substring(0, 10)}
                        {currentRecord.payment_reference && ` (Ref: ${currentRecord.payment_reference})`}
                      </span>
                    </>
                  ) : (
                    <>
                      <Clock className="w-5 h-5 text-amber-600" />
                      <span className="text-amber-700 dark:text-amber-400 font-medium">Payment Pending</span>
                    </>
                  )}
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t">
                {/* Primary action: Save or Mark as Paid */}
                {!currentRecord ? (
                  // No record exists yet - show Save Record
                  <Button onClick={handleSaveRecord} disabled={loading}>
                    <Receipt className="w-4 h-4 mr-2" />
                    Save Record
                  </Button>
                ) : isPaid ? (
                  // Already paid - show status
                  <Button disabled variant="outline" className="bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-400">
                    <Check className="w-4 h-4 mr-2" />
                    Paid ✓
                  </Button>
                ) : (
                  // Record exists but unpaid - show Mark as Paid prominently
                  <Button 
                    onClick={() => setShowPaymentDialog(true)} 
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Mark as Paid
                  </Button>
                )}
                
                <Button variant="outline" onClick={handleDownloadPDF}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Report
                </Button>
                
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { fetchDiagnostic(); setShowDiagnostic(!showDiagnostic); }}
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {showDiagnostic ? 'Hide' : 'Show'} Diagnostic
                </Button>
              </div>
              
              {/* Instructions for user */}
              {!currentRecord && calculation && calculation.royalty_amount > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>How to record your payment:</strong>
                  </p>
                  <ol className="text-sm text-blue-700 dark:text-blue-400 mt-2 space-y-1 list-decimal list-inside">
                    <li>Click <strong>&quot;Save Record&quot;</strong> to save this month&apos;s royalty calculation</li>
                    <li>After saving, click <strong>&quot;Mark as Paid&quot;</strong> to record your payment with date and reference number</li>
                    <li>The payment will appear in <strong>Payment History</strong> below</li>
                  </ol>
                </div>
              )}
              
              {currentRecord && !isPaid && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mt-4">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Payment Due:</strong> Click the green <strong>&quot;Mark as Paid&quot;</strong> button above to record your payment for {selectedMonth.replace('-', ' ')}.
                  </p>
                </div>
              )}
              
              {/* Database Diagnostic - Shows when $0 or manually triggered */}
              {showDiagnostic && diagnostic && (
                <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-medium">
                    <AlertCircle className="w-5 h-5" />
                    Database Diagnostic
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Database Name:</p>
                      <p className="font-mono font-medium">{diagnostic.database_config?.db_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Orders in DB:</p>
                      <p className="font-medium">{diagnostic.collection_counts?.orders}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Coffee Pass Sales:</p>
                      <p className="font-medium">{diagnostic.collection_counts?.coffee_club_sales}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Date Format Sample:</p>
                      <p className="font-mono text-xs">{diagnostic.raw_date_format_sample || 'N/A'}</p>
                    </div>
                  </div>
                  
                  {/* June 2026 Test Results - KEY FOR DEBUGGING */}
                  {diagnostic.june_2026_test && (
                    <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded text-sm">
                      <p className="font-medium text-blue-800 dark:text-blue-300 mb-1">June 2026 Query Test:</p>
                      <p className="text-blue-700 dark:text-blue-400">
                        Orders found: <strong>{diagnostic.june_2026_test.orders_found}</strong> | 
                        Coffee Passes: <strong>{diagnostic.june_2026_test.coffee_passes_found}</strong>
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{diagnostic.june_2026_test.query_used}</p>
                    </div>
                  )}
                  
                  {diagnostic.collection_counts?.orders === 0 && (
                    <div className="bg-red-100 dark:bg-red-900/40 p-3 rounded text-red-800 dark:text-red-300 text-sm">
                      <strong>⚠️ ISSUE FOUND:</strong> No orders in database. Your DB_NAME ({diagnostic.database_config?.db_name}) may not match where your sales data is stored.
                    </div>
                  )}
                  
                  {diagnostic.order_status_breakdown && Object.keys(diagnostic.order_status_breakdown).length > 0 && (
                    <div className="mt-2">
                      <p className="text-muted-foreground text-sm mb-2">Order Status Breakdown:</p>
                      <div className="space-y-1 text-xs font-mono">
                        {Object.entries(diagnostic.order_status_breakdown).map(([key, val]) => (
                          <div key={key} className="flex justify-between">
                            <span>{key}</span>
                            <span>{val.count} orders (${val.total})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {diagnostic.recent_orders && diagnostic.recent_orders.length > 0 && (
                    <div className="mt-2">
                      <p className="text-muted-foreground text-sm mb-2">Recent Orders (last 5):</p>
                      <div className="space-y-1 text-xs font-mono">
                        {diagnostic.recent_orders.slice(0, 5).map((o, i) => (
                          <div key={i} className="flex justify-between">
                            <span>{o.created_at?.substring(0, 10)}</span>
                            <span>{o.status}/{o.payment_status}</span>
                            <span>${o.total}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </CardContent>
      </Card>
      
      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Payment History
          </CardTitle>
          <CardDescription>
            Running total of all royalty payments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {Object.keys(historyByYear).length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No royalty records yet</p>
              <p className="text-sm">Select a month above and save a record to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(historyByYear)
                .sort(([a], [b]) => b.localeCompare(a))
                .map(([year, records]) => (
                  <div key={year} className="border rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
                      onClick={() => setExpandedYear(expandedYear === year ? null : year)}
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-muted-foreground" />
                        <span className="font-medium">{year}</span>
                        <span className="text-sm text-muted-foreground">
                          ({records.length} month{records.length !== 1 ? 's' : ''})
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm">
                          Total: <span className="font-medium">{formatCurrency(records.reduce((sum, r) => sum + (r.royalty_amount || 0), 0))} FJD</span>
                        </span>
                        {expandedYear === year ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </button>
                    
                    <AnimatePresence>
                      {expandedYear === year && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="divide-y">
                            {records.map(record => (
                              <div
                                key={record.id}
                                className="flex items-center justify-between p-4 hover:bg-muted/20 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-2 h-2 rounded-full ${
                                    record.status === 'paid' ? 'bg-green-500' : 'bg-amber-500'
                                  }`} />
                                  <div>
                                    <p className="font-medium">
                                      {new Date(record.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                      Sales: {formatCurrency(record.gross_sales)} FJD
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-medium">{formatCurrency(record.royalty_amount)} FJD</p>
                                  <p className={`text-sm ${
                                    record.status === 'paid' ? 'text-green-600' : 'text-amber-600'
                                  }`}>
                                    {record.status === 'paid' ? 'Paid' : 'Pending'}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Royalty Payment</DialogTitle>
            <DialogDescription>
              Mark the royalty for <strong>{new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</strong> as paid
            </DialogDescription>
          </DialogHeader>
          
          {/* Payment Amount Summary */}
          {calculation && (
            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground">Royalty Amount Due</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(calculation.royalty_amount)} FJD</p>
              <p className="text-xs text-muted-foreground mt-1">4% of {formatCurrency(calculation.sales_excluding_vat)} FJD net sales</p>
            </div>
          )}
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Payment Reference (optional)</Label>
              <Input
                placeholder="Check number, transfer reference, etc."
                value={paymentData.payment_reference}
                onChange={(e) => setPaymentData({ ...paymentData, payment_reference: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any additional notes..."
                value={paymentData.payment_notes}
                onChange={(e) => setPaymentData({ ...paymentData, payment_notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleMarkAsPaid} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
